import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import type { PreviewAutomationRuleResult } from 'hooks/orgEvents/orgEventAutomationHooks';
import { OrgEventListItem } from './OrgEventListItem';
import { toSyntheticEvent } from './rulePreviewEvent';

type RulePreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  error?: string | null;
  result?: PreviewAutomationRuleResult | null;
};

const NOT_FIRED_REASONS: Record<string, string> = {
  conditions_not_met:
    'No feature in the layer currently satisfies every criterion.',
  no_features: 'The feature layer returned no features to evaluate.',
  rule_disabled: 'This rule is switched off.',
  rule_not_found: 'This rule no longer exists.',
  placeholder_feed:
    'The Sensor Alpha feed is a placeholder and is not connected to live data.',
};

/**
 * Shows what the rule would create against live data. The backend dry-run
 * writes nothing and sends nothing.
 */
export function RulePreviewDialog({
  open,
  onOpenChange,
  isPending,
  error,
  result,
}: RulePreviewDialogProps) {
  const preview = result?.preview;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark tw:max-h-[85vh] tw:overflow-y-auto tw:bg-background tw:text-foreground sm:tw:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Preview</DialogTitle>
          <DialogDescription>
            Evaluated against live layer data. Nothing is saved and no
            notifications are sent.
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <div className="tw:space-y-2">
            <Skeleton className="tw:h-16 tw:w-full" />
            <Skeleton className="tw:h-24 tw:w-full" />
          </div>
        ) : error ? (
          <p className="tw:m-0 tw:text-sm tw:text-destructive">{error}</p>
        ) : !preview ? (
          <div className="tw:space-y-2">
            <p className="tw:m-0 tw:text-sm tw:font-medium">
              This rule would not create an event right now.
            </p>
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              {NOT_FIRED_REASONS[result?.reason ?? ''] ??
                'No matching feature was found.'}
            </p>
          </div>
        ) : (
          <div className="tw:space-y-3">
            {preview.isUpdate ? (
              <div className="tw:flex tw:items-start tw:gap-2 tw:rounded-md tw:border tw:border-amber-400/40 tw:bg-amber-400/10 tw:p-2">
                <AlertTriangle className="tw:mt-0.5 tw:h-3.5 tw:w-3.5 tw:shrink-0 tw:text-amber-500" />
                <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
                  A live run would update the existing open event for this
                  occurrence. It will not create a second event.
                </p>
              </div>
            ) : null}

            <OrgEventListItem
              event={toSyntheticEvent(preview)}
              defaultOpen
              scrollOnOpen={false}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
